/** Shared ABN lookup UI helpers for customer + business forms. */

import { lookupAbn } from "../api/mobile";
import { digitsOnly, normalizeAbn } from "../domain/address";
import { esc } from "../lib/escape";
import { openSheet, showToast } from "./ui";

export type AbnLookupResult = {
  abn: string;
  entity_name: string;
  abn_status?: string;
  gst_registered?: boolean;
  gst_from?: string | null;
  state?: string | null;
  postcode?: string | null;
};

export type AbnFormSnapshot = {
  name?: string;
  state?: string;
  postcode?: string;
  gst_registered?: boolean | null;
};

export type AbnDiscrepancy = {
  label: string;
  yours: string;
  register: string;
};

export type AbnLookupHandle = {
  getLastResult: () => AbnLookupResult | null;
  /** Lookup if needed for this ABN; returns null when blank / ABR unavailable. */
  ensureLookup: (abnRaw: string) => Promise<AbnLookupResult | null>;
};

export function abnLookupFieldHtml(opts: {
  name?: string;
  id?: string;
  value?: unknown;
  label?: string;
  required?: boolean;
  optionalMark?: boolean;
}): string {
  const name = opts.name || "abn";
  const id = opts.id || name;
  const label = opts.label || "ABN";
  const reqMarks = opts.optionalMark
    ? ` <span class="req-mark" data-abn-req ${opts.required ? "" : "hidden"}>*</span><span class="opt-mark" data-abn-opt ${opts.required ? "hidden" : ""}>(optional)</span>`
    : opts.required
      ? ` <span class="req-mark">*</span>`
      : "";
  return `
    <div class="field abn-lookup-field">
      <label for="${esc(id)}">${esc(label)}${reqMarks}</label>
      <div class="field-row abn-lookup-row">
        <input name="${esc(name)}" id="${esc(id)}" value="${esc(opts.value || "")}"
          inputmode="numeric" placeholder="11 digits" autocomplete="off">
        <button type="button" class="btn secondary" data-abn-lookup-btn>Look up</button>
      </div>
      <p class="hint abn-lookup-status" data-abn-lookup-status hidden></p>
      <div class="abn-mismatch" data-abn-mismatch hidden></div>
    </div>`;
}

function normalizeName(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Loose match so trading / shorthand names are not treated as equal. */
export function namesMatch(a: string, b: string): boolean {
  return normalizeName(a) === normalizeName(b);
}

function displayValue(v: string | boolean | null | undefined, empty = "—"): string {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  const s = String(v ?? "").trim();
  return s || empty;
}

export function compareAbnDetails(
  form: AbnFormSnapshot,
  register: AbnLookupResult
): AbnDiscrepancy[] {
  const diffs: AbnDiscrepancy[] = [];
  const formName = String(form.name || "").trim();
  const registerName = String(register.entity_name || "").trim();
  if (formName && registerName && !namesMatch(formName, registerName)) {
    diffs.push({
      label: "Name",
      yours: formName,
      register: registerName,
    });
  }

  const formState = String(form.state || "")
    .trim()
    .toUpperCase();
  const registerState = String(register.state || "")
    .trim()
    .toUpperCase();
  if (formState && registerState && formState !== registerState) {
    diffs.push({
      label: "State",
      yours: formState,
      register: registerState,
    });
  }

  const formPc = digitsOnly(form.postcode || "");
  const registerPc = digitsOnly(register.postcode || "");
  if (formPc && registerPc && formPc !== registerPc) {
    diffs.push({
      label: "Postcode",
      yours: formPc,
      register: registerPc,
    });
  }

  if (
    form.gst_registered != null &&
    register.gst_registered != null &&
    Boolean(form.gst_registered) !== Boolean(register.gst_registered)
  ) {
    diffs.push({
      label: "GST registered",
      yours: displayValue(Boolean(form.gst_registered)),
      register: displayValue(Boolean(register.gst_registered)),
    });
  }

  return diffs;
}

/** Prompt when form details differ from the ABN register — typo check, not a force-correct. */
export async function confirmAbnDiscrepancies(
  discrepancies: AbnDiscrepancy[],
  register: AbnLookupResult
): Promise<boolean> {
  if (!discrepancies.length) return true;
  const rows = discrepancies
    .map(
      (d) => `<tr>
        <th scope="row">${esc(d.label)}</th>
        <td>${esc(d.yours)}</td>
        <td>${esc(d.register)}</td>
      </tr>`
    )
    .join("");
  const result = await openSheet({
    title: "Check this ABN",
    bodyHtml: `
      <p class="hint">Your details differ from the ABN register. Trading or shorthand names are fine. Use this to catch a mistyped ABN.</p>
      <p class="abn-check-entity">Register shows <strong>${esc(register.entity_name || "unknown")}</strong>${
        register.abn_status ? ` (${esc(register.abn_status)})` : ""
      }.</p>
      <table class="abn-diff-table">
        <thead><tr><th></th><th>Yours</th><th>Register</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>`,
    actions: [
      { id: "back", label: "Go back", className: "btn secondary" },
      { id: "continue", label: "Continue anyway", className: "btn primary" },
    ],
  });
  return result === "continue";
}

/**
 * If an ABN is present, look it up and ask the user to confirm mismatches.
 * Returns false when the user cancels. ABR outages do not block save.
 */
export async function confirmAbnBeforeSave(opts: {
  abnRaw: string;
  form: AbnFormSnapshot;
  handle?: AbnLookupHandle | null;
}): Promise<boolean> {
  let abn = "";
  try {
    abn = normalizeAbn(opts.abnRaw);
  } catch {
    return true;
  }
  if (!abn) return true;

  let register: AbnLookupResult | null = null;
  try {
    register = opts.handle
      ? await opts.handle.ensureLookup(abn)
      : ((await lookupAbn(abn)) as AbnLookupResult);
  } catch {
    return true;
  }
  if (!register) return true;

  const diffs = compareAbnDetails(opts.form, register);
  if (!diffs.length) return true;
  return confirmAbnDiscrepancies(diffs, register);
}

function flashFilled(el: HTMLElement | null | undefined) {
  if (!el) return;
  el.classList.remove("abn-filled-flash");
  // Restart animation if the class was already present.
  void el.offsetWidth;
  el.classList.add("abn-filled-flash");
  window.setTimeout(() => el.classList.remove("abn-filled-flash"), 1600);
}

export function wireAbnLookup(opts: {
  root: ParentNode;
  abnInput: HTMLInputElement;
  nameInput: HTMLInputElement | null;
  stateSelect?: HTMLSelectElement | null;
  postcodeInput?: HTMLInputElement | null;
  gstSelect?: HTMLSelectElement | null;
  onLookedUp?: (result: AbnLookupResult) => void;
}): AbnLookupHandle {
  const btn = opts.root.querySelector("[data-abn-lookup-btn]") as HTMLButtonElement | null;
  const statusEl = opts.root.querySelector("[data-abn-lookup-status]") as HTMLElement | null;
  const mismatchEl = opts.root.querySelector("[data-abn-mismatch]") as HTMLElement | null;

  let lastResult: AbnLookupResult | null = null;
  let lastAbn = "";
  let inflight: Promise<AbnLookupResult | null> | null = null;

  const setStatus = (text: string, show: boolean) => {
    if (!statusEl) return;
    statusEl.textContent = text;
    statusEl.hidden = !show;
  };

  const clearMismatch = () => {
    if (!mismatchEl) return;
    mismatchEl.hidden = true;
    mismatchEl.innerHTML = "";
  };

  const showNameNote = (registerName: string) => {
    if (!mismatchEl || !opts.nameInput) return;
    mismatchEl.hidden = false;
    mismatchEl.innerHTML = `
      <p class="hint">Register name is <strong>${esc(registerName)}</strong>. Keeping your name is fine if it's a trading or shorthand name. We'll ask you to confirm the ABN on save if it still differs.</p>`;
  };

  const applyResult = (result: AbnLookupResult) => {
    const filled: string[] = [];
    const entity = String(result.entity_name || "").trim();

    if (opts.nameInput && entity) {
      const current = String(opts.nameInput.value || "").trim();
      if (!current) {
        opts.nameInput.value = entity;
        flashFilled(opts.nameInput);
        filled.push("name");
      } else if (!namesMatch(current, entity)) {
        showNameNote(entity);
      }
    }

    const registerState = String(result.state || "")
      .trim()
      .toUpperCase();
    if (opts.stateSelect && registerState) {
      const current = String(opts.stateSelect.value || "")
        .trim()
        .toUpperCase();
      if (!current) {
        const hasOption = Array.from(opts.stateSelect.options).some((o) => o.value === registerState);
        if (hasOption) {
          opts.stateSelect.value = registerState;
          flashFilled(opts.stateSelect);
          filled.push("state");
        }
      }
    }

    const registerPc = digitsOnly(result.postcode || "");
    if (opts.postcodeInput && registerPc) {
      const current = digitsOnly(opts.postcodeInput.value);
      if (!current) {
        opts.postcodeInput.value = registerPc;
        flashFilled(opts.postcodeInput);
        filled.push("postcode");
      }
    }

    if (opts.gstSelect && result.gst_registered != null) {
      const next = result.gst_registered ? "yes" : "no";
      if (opts.gstSelect.value !== next) {
        opts.gstSelect.value = next;
        opts.gstSelect.dispatchEvent(new Event("change", { bubbles: true }));
        flashFilled(opts.gstSelect);
        filled.push(result.gst_registered ? "GST yes" : "GST no");
      } else {
        flashFilled(opts.gstSelect);
      }
    }

    const where =
      [registerState, registerPc].filter(Boolean).join(" ") || "";
    const fillNote = filled.length ? ` · Filled ${filled.join(", ")}` : "";
    setStatus(
      entity
        ? `Found: ${entity}${result.abn_status ? ` (${result.abn_status})` : ""}${
            where ? ` · ${where}` : ""
          }${fillNote}`
        : `ABN found on the register.${fillNote}`,
      true
    );
  };

  const fetchResult = async (abn: string): Promise<AbnLookupResult | null> => {
    if (lastResult && lastAbn === abn) return lastResult;
    if (inflight) return inflight;
    inflight = (async () => {
      try {
        const result = (await lookupAbn(abn)) as AbnLookupResult;
        lastResult = result;
        lastAbn = abn;
        return result;
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  };

  const runLookup = async () => {
    clearMismatch();
    let abn = "";
    try {
      abn = normalizeAbn(opts.abnInput.value);
    } catch (ex) {
      setStatus(ex instanceof Error ? ex.message : "Enter an 11-digit ABN.", true);
      return;
    }
    opts.abnInput.value = abn;
    if (btn) btn.disabled = true;
    setStatus("Looking up…", true);
    try {
      // Force a fresh lookup on explicit Look up / blur.
      lastResult = null;
      lastAbn = "";
      const result = await fetchResult(abn);
      if (!result) throw new Error("ABN lookup failed.");
      applyResult(result);
      opts.onLookedUp?.(result);
    } catch (ex) {
      lastResult = null;
      lastAbn = "";
      setStatus(ex instanceof Error ? ex.message : "ABN lookup failed.", true);
      showToast(ex instanceof Error ? ex.message : "ABN lookup failed.", "error");
    } finally {
      if (btn) btn.disabled = false;
    }
  };

  btn?.addEventListener("click", () => {
    void runLookup();
  });
  opts.abnInput.addEventListener("blur", () => {
    const digits = digitsOnly(opts.abnInput.value);
    if (digits.length === 11) void runLookup();
  });
  opts.abnInput.addEventListener("input", () => {
    const digits = digitsOnly(opts.abnInput.value);
    if (lastAbn && digits !== lastAbn) {
      lastResult = null;
      lastAbn = "";
      clearMismatch();
    }
  });

  return {
    getLastResult: () => lastResult,
    ensureLookup: async (abnRaw: string) => {
      let abn = "";
      try {
        abn = normalizeAbn(abnRaw);
      } catch {
        return null;
      }
      if (!abn) return null;
      try {
        return await fetchResult(abn);
      } catch {
        return null;
      }
    },
  };
}
